import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useContent } from '../contexts/ContentContext';
import { useMedia } from '../contexts/MediaContext';
import { LazyVideo } from './LazyVideo';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useDeviceProfile } from '../hooks/useDeviceProfile';
import { getAssetUrls } from '../utils/media';
import { STATIC_VIDEOS } from '../config/staticMedia';

interface ValueItem {
  title: string;
  description: string;
}

type ReelItem = {
  id: string;
  type: 'image' | 'video';
  src: string;
  poster?: string;
  thumbnail?: string;
  title: string;
  label: string;
  description: string;
};

const AUTO_ADVANCE_MS = 6500;
const FALLBACK_THUMB_LABELS = ['AI Chat', 'Content', '3D Models'];
const GRADIENT_CLASSES = [
  'bg-[linear-gradient(150deg,_rgba(79,70,229,0.6)_0%,_rgba(14,165,233,0.35)_100%)]',
  'bg-[linear-gradient(160deg,_rgba(236,72,153,0.45)_0%,_rgba(248,113,113,0.35)_100%)]',
  'bg-[linear-gradient(140deg,_rgba(16,185,129,0.45)_0%,_rgba(132,204,22,0.3)_100%)]',
];

export const ValueProps = () => {
  const { t } = useTranslation();
  const { getValue } = useContent();
  const { getByPlacement } = useMedia();

  const baseItems = t('vantaggi.items', { returnObjects: true }) as ValueItem[];
  const items = baseItems.map((item, idx) => ({
    title: getValue(`vantaggi.items.${idx}.title`, item.title),
    description: getValue(`vantaggi.items.${idx}.description`, item.description),
  }));

  const eyebrow = getValue('vantaggi.eyebrow', t('vantaggi.eyebrow'));
  const title = getValue('vantaggi.title', t('vantaggi.title'));
  const subtitle = getValue('vantaggi.subtitle', t('vantaggi.subtitle'));

  const mediaOverline = t('value.media.overline');
  const mediaHeadline = t('value.media.headline');
  const mediaBody = t('value.media.body');
  const mediaBadges = t('value.media.badges', { returnObjects: true, defaultValue: ['Data secured', 'Teaching control', 'Offline stability'] }) as string[];

  const mediaVideoTitle = t('value.media.videoTitle');
  const mediaVideoBadge = t('value.media.videoBadge');
  const thumbLabels = t('value.media.thumbLabels', { returnObjects: true, defaultValue: FALLBACK_THUMB_LABELS }) as string[];

  const hookAssets = getByPlacement('valueprops.emotional_hook');
  const prefersReducedMotion = useReducedMotion();
  const { isMobile } = useDeviceProfile();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const resumeTimeoutRef = useRef<number | null>(null);

  const reelItems = useMemo(() => {
    const fallbackLabels = thumbLabels.length > 0 ? thumbLabels : FALLBACK_THUMB_LABELS;
    
    // Use dynamic assets if available, otherwise use static videos
    if (hookAssets.length > 0) {
      return hookAssets
        .map<ReelItem | null>((asset, idx) => {
          const urls = getAssetUrls(asset);
          if (!urls.mediaUrl) {
            return null;
          }
          const fallbackLabel = fallbackLabels[idx % fallbackLabels.length] ?? `Clip ${idx + 1}`;
          return {
            id: asset.id ?? `asset-${idx}`,
            type: asset.type,
            src: urls.mediaUrl,
            poster: urls.thumbnailUrl,
            thumbnail: urls.thumbnailUrl || urls.mediaUrl,
            title: asset.title && asset.title.length > 0 ? asset.title : fallbackLabel,
            label: fallbackLabel,
            description: asset.description ?? '',
          };
        })
        .filter((item): item is ReelItem => item !== null);
    }
    
    // Fallback to static videos
    return STATIC_VIDEOS.valueProps.map(video => ({
      ...video,
      poster: undefined,
      thumbnail: undefined,
    }));
  }, [hookAssets, thumbLabels]);

  const hasInteractiveReel = reelItems.length > 0;
  const fallbackThumbnails = (thumbLabels.length > 0 ? thumbLabels : FALLBACK_THUMB_LABELS).slice(0, 3);
  const activeItem = reelItems[activeIndex] ?? null;

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const temporarilyPause = useCallback(
    (duration = AUTO_ADVANCE_MS) => {
      setIsInteracting(true);
      clearResumeTimeout();
      resumeTimeoutRef.current = window.setTimeout(() => {
        setIsInteracting(false);
        resumeTimeoutRef.current = null;
      }, duration);
    },
    [clearResumeTimeout],
  );

  const handleClipSelect = useCallback(
    (index: number) => {
      if (reelItems.length === 0) return;
      const safeIndex = ((index % reelItems.length) + reelItems.length) % reelItems.length;
      setActiveIndex(safeIndex);
      setProgress(0);
      temporarilyPause();
    },
    [reelItems.length, temporarilyPause],
  );

  const handleMouseEnter = useCallback(() => {
    clearResumeTimeout();
    setIsInteracting(true);
  }, [clearResumeTimeout]);

  const handleMouseLeave = useCallback(() => {
    clearResumeTimeout();
    setIsInteracting(false);
  }, [clearResumeTimeout]);

  const handleTouchStart = useCallback(() => {
    handleMouseEnter();
  }, [handleMouseEnter]);

  const handleTouchEnd = useCallback(() => {
    temporarilyPause();
  }, [temporarilyPause]);

  const handleTouchCancel = useCallback(() => {
    handleMouseLeave();
  }, [handleMouseLeave]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (reelItems.length <= 1) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleClipSelect(activeIndex === 0 ? reelItems.length - 1 : activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleClipSelect((activeIndex + 1) % reelItems.length);
      }
    },
    [activeIndex, reelItems.length, handleClipSelect],
  );

  useEffect(() => {
    return () => {
      clearResumeTimeout();
    };
  }, [clearResumeTimeout]);

  useEffect(() => {
    if (reelItems.length === 0) {
      if (activeIndex !== 0) {
        setActiveIndex(0);
      }
      return;
    }
    if (activeIndex >= reelItems.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, reelItems.length]);

  useEffect(() => {
    setProgress(0);
  }, [activeIndex]);

  useEffect(() => {
    if (prefersReducedMotion || reelItems.length <= 1 || isInteracting || isMobile) {
      return;
    }

    let frameId: number;
    const start = performance.now();

    const step = (now: number) => {
      const elapsed = now - start;
      const ratio = Math.min(elapsed / AUTO_ADVANCE_MS, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        setActiveIndex((prev) => (prev + 1) % reelItems.length);
      } else {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [prefersReducedMotion, reelItems.length, isInteracting, activeIndex, isMobile]);

  return (
    <section id="vantaggi" data-section="value-props" className="py-14 bg-regai-surface-alt animate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase tracking-widest text-xs font-semibold text-regai-accent mb-2">
            {eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold leading-tight">{title}</h2>
          <p className="mt-3 text-regai-muted text-lg">{subtitle}</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 card-group">
          {items.map((item, idx) => (
            <div key={idx} className="rounded-2xl border border-regai bg-regai-surface p-6">
              <h3 className="font-bold">{item.title}</h3>
              <p className="text-regai-muted mt-2">{item.description}</p>
            </div>
          ))}
        </div>

        <section
          aria-labelledby="valueprops-media"
          className="mt-14 overflow-hidden rounded-[40px] border border-white/10 bg-[linear-gradient(135deg,rgba(40,80,245,0.25)_0%,rgba(91,63,246,0.18)_45%,rgba(192,60,247,0.14)_100%)] p-8 lg:p-12 shadow-[0_30px_90px_-45px_rgba(14,18,52,0.7)]"
        >
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] items-center">
            <div className="space-y-4 text-white">
              <p className="uppercase text-xs font-semibold tracking-[0.3em] text-regai-accent" id="valueprops-media">
                {mediaOverline}
              </p>
              <h3 className="text-2xl lg:text-[2.4rem] font-bold leading-snug">{mediaHeadline}</h3>
              <p className="text-sm lg:text-base text-slate-200/80 max-w-xl">{mediaBody}</p>
              <div className="flex flex-wrap gap-3 text-xs font-medium text-regai-accent">
                {mediaBadges.map((badge, idx) => (
                  <span
                    key={`${badge}-${idx}`}
                    className={
                      idx === 0
                        ? 'inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1'
                        : 'inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-white'
                    }
                  >
                    {idx === 0 && <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />}
                    {badge}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <div
                className="absolute -top-12 -left-12 h-48 w-48 rounded-full bg-[rgba(192,60,247,0.37)] blur-3xl"
                aria-hidden="true"
              />
              <div
                className="absolute -bottom-16 -right-6 h-40 w-40 rounded-full bg-[rgba(59,130,246,0.35)] blur-3xl"
                aria-hidden="true"
              />

              {hasInteractiveReel ? (
                <div
                  className="group relative overflow-hidden rounded-3xl border border-white/15 bg-slate-900/70 backdrop-blur focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  tabIndex={0}
                  aria-label="Carosello interattivo dei media"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onFocusCapture={handleMouseEnter}
                  onBlurCapture={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                  onKeyDown={handleKeyDown}
                >
                  <div className="relative aspect-video w-full overflow-hidden">
                    {activeItem ? (
                      activeItem.type === 'video' ? (
                        <LazyVideo
                          key={activeItem.id}
                          src={activeItem.src}
                          poster={activeItem.poster}
                          className="absolute inset-0 h-full w-full object-cover"
                          controls
                        />
                      ) : (
                        <img
                          key={activeItem.id}
                          src={activeItem.src}
                          alt={activeItem.title}
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                        />
                      )
                    ) : null}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-transparent" aria-hidden="true" />

                    <div className="pointer-events-none absolute top-4 left-4 right-4 flex gap-2" aria-hidden="true">
                      {reelItems.map((item, idx) => {
                        const segmentProgress = idx < activeIndex ? 1 : idx === activeIndex ? Math.min(progress, 1) : 0;
                        return (
                          <div key={item.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/20">
                            <div
                              className="h-full origin-left bg-white transition-transform duration-150 ease-out"
                              style={{ transform: `scaleX(${segmentProgress})` }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="pointer-events-none absolute bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 flex items-end justify-between gap-2 sm:gap-4 text-white">
                      <div className="max-w-[70%]">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">
                          {activeItem?.label ?? mediaVideoTitle}
                        </p>
                        <p className="mt-1 text-lg font-semibold leading-tight md:text-xl">
                          {activeItem?.title ?? mediaVideoTitle}
                        </p>
                        {activeItem?.description ? (
                          <p className="mt-2 text-xs text-white/70 line-clamp-3">{activeItem.description}</p>
                        ) : null}
                      </div>
                      {mediaVideoBadge ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-300">
                          <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                          {mediaVideoBadge}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {reelItems.length > 1 ? (
                    <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-3">
                      <button
                        type="button"
                        className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/30 text-white transition hover:bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
                        aria-label="Clip precedente"
                        onClick={() => handleClipSelect(activeIndex === 0 ? reelItems.length - 1 : activeIndex - 1)}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/30 text-white transition hover:bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
                        aria-label="Clip successiva"
                        onClick={() => handleClipSelect((activeIndex + 1) % reelItems.length)}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  ) : null}
                  <span className="sr-only" aria-live="polite">
                    {activeItem?.title ?? mediaVideoTitle}
                  </span>
                </div>
              ) : (
                <figure className="relative overflow-hidden rounded-3xl border border-white/15 bg-slate-900/70 backdrop-blur">
                  <div className="relative aspect-video">
                    <div
                      className="absolute inset-0 bg-[radial-gradient(circle_at_top,var(--color-gradient-middle)_0%,rgba(10,12,32,0.88)_85%)]"
                      aria-hidden="true"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
                        <span
                          className="ml-1 inline-block h-10 w-10 border-y-[14px] border-l-[22px] border-solid border-y-transparent border-l-white"
                          aria-hidden="true"
                        />
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between text-xs font-semibold text-white/80">
                      <span>{mediaVideoTitle}</span>
                      <span className="inline-flex items-center gap-1 text-emerald-300">
                        <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                        {mediaVideoBadge}
                      </span>
                    </div>
                  </div>
                </figure>
              )}

              {hasInteractiveReel ? (
                <div className="mt-5 flex gap-3 overflow-x-auto pb-2">
                  {reelItems.map((item, idx) => {
                    const isActive = idx === activeIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`relative aspect-[4/5] w-20 sm:w-[7.5rem] shrink-0 overflow-hidden rounded-2xl border transition-all duration-200 ${
                          isActive ? 'border-white/70 ring-2 ring-white/60' : 'border-white/15 hover:border-white/30'
                        }`}
                        onClick={() => handleClipSelect(idx)}
                        onFocus={handleMouseEnter}
                        onBlur={handleMouseLeave}
                      >
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt={item.title} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div
                            className={`h-full w-full ${GRADIENT_CLASSES[idx % GRADIENT_CLASSES.length]}`}
                            aria-hidden="true"
                          />
                        )}
                        <span className="absolute inset-0 bg-white/10 mix-blend-overlay" aria-hidden="true" />
                        <span className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white drop-shadow">
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-3 gap-3">
                  {fallbackThumbnails.map((label, idx) => (
                    <div
                      key={`${label}-${idx}`}
                      className={`relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/15 ${GRADIENT_CLASSES[idx % GRADIENT_CLASSES.length]}`}
                    >
                      <span className="absolute inset-0 bg-white/10 mix-blend-overlay" aria-hidden="true" />
                      <span className="absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/80">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};
